import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress, Fade } from '@mui/material';
import {
  Code,
  Storage,
  Psychology,
  Timeline,
  CheckCircle,
  BugReport,
  Search
} from '@mui/icons-material';

const steps = [
  { icon: <Code />, text: "Lecture de la structure du projet..." },
  { icon: <Storage />, text: "Analyse des données des tâches..." },
  { icon: <Search />, text: "Identification des points bloquants..." },
  { icon: <Timeline />, text: "Calcul des probabilités de retard..." },
  { icon: <BugReport />, text: "Détection des risques potentiels..." },
  { icon: <Psychology />, text: "Génération des suggestions IA..." },
  { icon: <CheckCircle />, text: "Finalisation du rapport..." }
];

const AILoadingAnimation = () => {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => {
        if (prev < steps.length - 1) {
          return prev + 1;
        }
        return prev; // Stay on last step until complete
      });
    }, 1500); // Change step every 1.5s

    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8,
      maxWidth: 500,
      mx: 'auto'
    }}>
      {/* Animated Brain/AI Icon */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Psychology sx={{ fontSize: 80, color: 'primary.main', opacity: 0.2 }} />
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'pulse 2s infinite ease-in-out',
          '@keyframes pulse': {
            '0%': { transform: 'scale(0.95)', opacity: 0.5 },
            '50%': { transform: 'scale(1.1)', opacity: 1 },
            '100%': { transform: 'scale(0.95)', opacity: 0.5 },
          }
        }}>
          <Psychology sx={{ fontSize: 80, color: 'primary.main' }} />
        </Box>
      </Box>

      {/* Steps Display */}
      <Box sx={{ width: '100%', mb: 2 }}>
        {steps.map((step, index) => (
          <Fade in={activeStep === index} timeout={500} unmountOnExit key={index}>
            <Box sx={{ 
              display: activeStep === index ? 'flex' : 'none', 
              flexDirection: 'column', 
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'medium', color: 'primary.main' }}>
                {step.text}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, color: 'text.secondary' }}>
                {step.icon}
                <Typography variant="caption">Étape {index + 1}/{steps.length}</Typography>
              </Box>
            </Box>
          </Fade>
        ))}
      </Box>

      {/* Progress Bar */}
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress 
          variant="determinate" 
          value={((activeStep + 1) / steps.length) * 100} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: 'rgba(0,0,0,0.05)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundImage: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            }
          }}
        />
      </Box>
      
      {/* History of steps (fading out) */}
      <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.6, width: '100%' }}>
        {steps.map((step, index) => (
          index < activeStep && (
            <Fade in={true} key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main' }}>
                <CheckCircle sx={{ fontSize: 16 }} />
                <Typography variant="caption" sx={{ textDecoration: 'line-through' }}>
                  {step.text}
                </Typography>
              </Box>
            </Fade>
          )
        ))}
      </Box>
    </Box>
  );
};

export default AILoadingAnimation;
